import { FC, useContext, useEffect, useState } from 'react';
import { Button, Flex, Image, Tooltip } from 'src/components/ui';
import { DownloadIcon, UploadIcon } from 'src/assets/icons';
import { ImageLayer, TextLayer } from 'src/lib/schemas';
import { customToast, toastError } from 'src/services/toast';
import { CampaignContext } from 'src/contexts';
import { delay, downloadFile, parseCampaignName } from 'src/lib/utils';
import http from 'src/services/http';
import { uploadFile } from 'src/services/fileUpload';
import CustomInput from '../CustomInput';

interface IPerview {
	flatFile: string;
	layeredFile: string;
	layers: (ImageLayer | TextLayer)[];
}

interface MediaInputHookProps {
	layer: ImageLayer;
	isLocked?: boolean;
	previewPSD: IPerview;
	control?: any;
	onShowClick?: () => void;
	onChangePreviewPSD?: (
		flatFile: string,
		layeredFile: string,
		layers: (ImageLayer | TextLayer)[],
	) => void;
	onChangingLayer: (status: boolean) => void;
}

const MediaInputHook: FC<MediaInputHookProps> = ({
	layer,
	isLocked,
	previewPSD,
	control,
	onShowClick,
	onChangePreviewPSD,
	onChangingLayer,
}) => {
	const {
		name,
		displayName,
		imageUrl,
		dimensions: { width, height },
	} = layer;
	const accept = '.png, .jpg, .jpeg, .svg, .webp, .gif, .avif';

	const [isUploading, setIsUploading] = useState(false);
	const [isDownloading, setDownloading] = useState(false);
	const [fileToSave, setFileToSave] = useState<File | undefined>(undefined);
	const [imageSrc, setImageSrc] = useState<string | undefined>(imageUrl);
	const { campaign } = useContext(CampaignContext);

	useEffect(() => {
		setImageSrc(imageUrl);
	}, [imageUrl]);

	useEffect(() => {
		if (isUploading || !fileToSave) return;
		handleUploadFile(fileToSave);
		setFileToSave(undefined);
	}, [fileToSave]);

	const cleanS3ImageUrl = (url: string) => {
		const baseUrl = url.split('?')[0];
		if (baseUrl.match(/\.(jpg|jpeg|png|gif)$/i)) return baseUrl;
		else return '';
	};

	const handleDownload = async () => {
		setDownloading(true);
		if (!imageSrc) {
			customToast('No file to download', 'error');
			return;
		}

		const fileName = `${parseCampaignName(
			campaign?.title ?? '',
		)}-design-direction-${name}-${width}x${height}`;
		try {
			await downloadFile(cleanS3ImageUrl(imageSrc), fileName);
		} catch (error) {
			toastError(error);
		} finally {
			setDownloading(false);
		}
	};

	const handleUploadFile = async (file: File) => {
		if (!isValidFile(file)) {
			customToast(
				`Unsupported file format. Only .png and .jpg allowed.`,
				'error',
			);
			return;
		}

		setIsUploading(true);
		onChangingLayer(true);

		try {
			let uploadedImage;
			if (layer.name === '$account.logo') {
				const minWidth = 100;
				const minHeight = 30;
				uploadedImage = await uploadFile(file, true, minWidth, minHeight, 100, 400, false, false, false);
			} else if (
				layer.name === '$promotedObject.image' ||
				layer.name === 'Product'
			) {
				const minShortestSide = 100;
				const minLongestSide = 400;
				uploadedImage = await uploadFile(
					file,
					true,
					undefined,
					undefined,
					minShortestSide,
					minLongestSide,
				);
			} else {
				uploadedImage = await uploadFile(file, true, 100, 100, 100, 400, false, false, false);
			}

			uploadedImage.imageUrl && changeLayer(uploadedImage.imageUrl);
		} catch (error) {
			toastError(error);
			setIsUploading(false);
			onChangingLayer(false);
		}
	};

	const changeLayer = async (image: string) => {
		const changeLayerPayload = {
			inputs: {
				layeredFile: previewPSD.layeredFile,
				values: [
					{
						layer: layer.name,
						property: 'image',
						value: image,
					},
				],
			},
		};

		try {
			const { data } = await http.post(
				'/v2/apps/fusion_ai.edit_psd/execute/edit_psd',
				changeLayerPayload,
			);
			if (data) await processCallback(data.callback, image);
		} catch (error) {
			toastError(error);
			setIsUploading(false);
			onChangingLayer(false);
		}
	};

	const processCallback = async (callbackUrl: string, image: string) => {
		try {
			const response = await http.get(callbackUrl);
			const { status, body } = response.data;
			if (status === 'processing' || status === 'pending') {
				setTimeout(processCallback.bind(null, callbackUrl, image), 500);
			} else if (status === 'error' || status === 'failed') {
				customToast('Error processing image', 'error');
				onChangingLayer(false);
				setIsUploading(false);
			} else if (status === 'successful') {
				const updatedLayer = { ...layer, imageUrl: image };
				const filterdLayers = previewPSD.layers.filter(
					(layer) => layer.name !== name,
				);
				const layers = [...filterdLayers, updatedLayer];
				onChangePreviewPSD?.(body.flatFile, body.layeredFile, layers);
				delay(100);
				setImageSrc(image);
				onChangingLayer(false);
				setIsUploading(false);
			}
		} catch (e) {
			toastError(e);
			onChangingLayer(false);
			setIsUploading(false);
		}
	};

	const isValidFile = (file: File): boolean => {
		const allowedExtensions = Array.from(
			new Set(accept.split(',').map((ext) => ext.trim().substring(1))),
		);
		const fileExtension = file.name.split('.').pop()?.toLowerCase();
		return fileExtension ? allowedExtensions.includes(fileExtension) : false;
	};

	return (
		<Flex gap={4} direction="column">
			<CustomInput
				image={imageSrc}
				label={displayName}
				name={name}
				control={control}
				isLayerVisible={layer.visible}
				onShowClick={onShowClick}
				onDownloadClick={handleDownload}
				onUploadClick={(file: File) => setFileToSave(file)}
				isLoading={isUploading}
			/>
		</Flex>
	);
};

export default MediaInputHook;
